import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, ExternalLink, Loader2, Search, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Textarea } from '../../../components/ui/textarea';
import KriterienPruefung from '../../../components/abnahme/KriterienPruefung';
import StoryStatusBadge from '../../../components/abnahme/StoryStatusBadge';
import StoryTicketListe from '../../../components/abnahme/StoryTicketListe';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  abnahmeApi,
  type FreigabeStoryDto,
  type FreigabeUebersichtDto,
  type FremdTicketDto,
  type StoryTicketDto,
} from '../../../lib/api/abnahme';

const datum = (iso: string | null): string => {
  if (!iso) return '—';
  const wert = new Date(iso);
  return Number.isNaN(wert.getTime()) ? '—' : wert.toLocaleString('de-DE');
};

/** Die Abdeckung in einem Satz — ohne Laufdetails, die hier niemand braucht. */
function pruefSatz(story: FreigabeStoryDto): string {
  const { tests } = story;
  if (tests.status === 'keine') return 'Noch kein Test belegt diese Story.';
  const kriterien =
    tests.kriterien > 0 ? `${tests.abgedeckt} von ${tests.kriterien} Kriterien geprüft` : 'geprüft';
  if (tests.status === 'rot') {
    return `${kriterien} — ${tests.fehlgeschlagen} ${tests.fehlgeschlagen === 1 ? 'Prüfung ist' : 'Prüfungen sind'} derzeit rot.`;
  }
  const offen = tests.kriterien - tests.abgedeckt;
  return offen > 0
    ? `${kriterien}, ${offen} noch offen — bisher nichts rot.`
    : `${kriterien}, zuletzt alles grün.`;
}

interface Stand {
  gesamt: number;
  geprueft: number;
  rot: number;
  offen: number;
}

/** Prüfstand der Kriterien — je Kriterium, wenn das Backend ihn liefert, sonst aus der Story-Abdeckung. */
function kriterienStand(story: FreigabeStoryDto): Stand {
  const mitPruefung = story.kriterien.filter((kriterium) => kriterium.pruefung);
  if (mitPruefung.length === story.kriterien.length && story.kriterien.length > 0) {
    const geprueft = mitPruefung.filter((k) => k.pruefung?.status === 'gruen').length;
    const rot = mitPruefung.filter((k) => k.pruefung?.status === 'rot').length;
    return { gesamt: story.kriterien.length, geprueft, rot, offen: story.kriterien.length - geprueft - rot };
  }
  const gesamt = story.tests.kriterien || story.kriterien.length;
  const rot = story.tests.status === 'rot' ? 1 : 0;
  const geprueft = Math.max(0, story.tests.abgedeckt - rot);
  return { gesamt, geprueft, rot, offen: Math.max(0, gesamt - geprueft - rot) };
}

const hatProblem = (story: FreigabeStoryDto, stand: Stand): boolean =>
  stand.rot > 0 || story.tests.status === 'rot' || story.tickets.blocker > 0 || story.tickets.open > 0;

/** Alles geprüft, nichts rot, keine offene Meldung — kann abgenommen werden. */
const istAbnahmereif = (story: FreigabeStoryDto, stand: Stand): boolean =>
  stand.offen === 0 && !hatProblem(story, stand);

type Filter = 'alle' | 'abnahmereif' | 'ungeprueft' | 'probleme';

const FILTER: Array<{ key: Filter; label: string }> = [
  { key: 'alle', label: 'Alle' },
  { key: 'abnahmereif', label: 'Vollständig geprüft' },
  { key: 'ungeprueft', label: 'Noch zu prüfen' },
  { key: 'probleme', label: 'Rot oder Meldungen' },
];

function passtZumFilter(story: FreigabeStoryDto, filter: Filter): boolean {
  const stand = kriterienStand(story);
  if (filter === 'abnahmereif') return istAbnahmereif(story, stand);
  if (filter === 'ungeprueft') return stand.offen > 0;
  if (filter === 'probleme') return hatProblem(story, stand);
  return true;
}

function passtZurSuche(story: FreigabeStoryDto, suche: string): boolean {
  if (!suche) return true;
  const text = [story.key, story.title, story.description, ...story.kriterien.map((k) => k.text)]
    .join(' ')
    .toLowerCase();
  return suche
    .toLowerCase()
    .split(/\s+/)
    .every((wort) => text.includes(wort));
}

interface Bereich {
  key: string;
  title: string;
  stories: FreigabeStoryDto[];
}

/** Stories nach Epic gruppieren — in der Reihenfolge, in der das Backend sie liefert. */
function nachBereich(stories: FreigabeStoryDto[]): Bereich[] {
  const bereiche = new Map<string, Bereich>();
  for (const story of stories) {
    const bereich = bereiche.get(story.epic.key) ?? { key: story.epic.key, title: story.epic.title, stories: [] };
    bereich.stories.push(story);
    bereiche.set(story.epic.key, bereich);
  }
  return [...bereiche.values()];
}

/** Schmaler Balken: geprüft (grün), rot, offen (grau). */
function StandBalken({ stand, className = '' }: { stand: Stand; className?: string }) {
  if (stand.gesamt === 0) return <div className={`h-1.5 rounded-full bg-muted ${className}`} />;
  const anteil = (wert: number) => `${(wert / stand.gesamt) * 100}%`;
  return (
    <div className={`flex h-1.5 overflow-hidden rounded-full bg-muted ${className}`} aria-hidden>
      <div className="bg-emerald-500" style={{ width: anteil(stand.geprueft) }} />
      <div className="bg-red-500" style={{ width: anteil(stand.rot) }} />
    </div>
  );
}

function summe(stories: FreigabeStoryDto[]): Stand {
  return stories.map(kriterienStand).reduce(
    (a, b) => ({ gesamt: a.gesamt + b.gesamt, geprueft: a.geprueft + b.geprueft, rot: a.rot + b.rot, offen: a.offen + b.offen }),
    { gesamt: 0, geprueft: 0, rot: 0, offen: 0 },
  );
}

/** Kopf der Seite: wie weit die Abnahme insgesamt ist. */
function Gesamtstand({ daten }: { daten: FreigabeUebersichtDto }) {
  const abgenommen = daten.entschieden.filter((s) => s.status === 'abgenommen').length;
  const abgelehnt = daten.entschieden.filter((s) => s.status === 'abgelehnt').length;
  const offen = daten.bereit.length;
  const gesamt = abgenommen + abgelehnt + offen;
  const reif = daten.bereit.filter((s) => istAbnahmereif(s, kriterienStand(s))).length;
  const anteil = (wert: number) => (gesamt ? `${(wert / gesamt) * 100}%` : '0%');
  const kacheln = [
    { label: 'Abgenommen', wert: abgenommen, farbe: 'text-emerald-700 dark:text-emerald-400' },
    { label: 'Zur Abnahme', wert: offen, farbe: '' },
    { label: 'davon vollständig geprüft', wert: reif, farbe: '' },
    { label: 'Abgelehnt', wert: abgelehnt, farbe: abgelehnt ? 'text-red-600 dark:text-red-400' : '' },
  ];
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {kacheln.map((kachel) => (
          <div key={kachel.label}>
            <div className={`text-2xl font-semibold tabular-nums ${kachel.farbe}`}>{kachel.wert}</div>
            <div className="text-xs text-muted-foreground">{kachel.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="bg-emerald-500" style={{ width: anteil(abgenommen) }} />
        <div className="bg-red-500" style={{ width: anteil(abgelehnt) }} />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {gesamt ? `${Math.round((abgenommen / gesamt) * 100)} % abgenommen` : 'Noch nichts zur Abnahme gemeldet'}
      </p>
    </div>
  );
}

/** Kurzer Prüfstand für die zugeklappte Zeile. */
function StandHinweis({ story, stand }: { story: FreigabeStoryDto; stand: Stand }) {
  if (hatProblem(story, stand)) {
    const teile: string[] = [];
    if (stand.rot > 0 || story.tests.status === 'rot') teile.push('rot');
    if (story.tickets.blocker > 0) teile.push(`${story.tickets.blocker} Blocker`);
    else if (story.tickets.open > 0) teile.push(`${story.tickets.open} ${story.tickets.open === 1 ? 'Meldung' : 'Meldungen'}`);
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
        <AlertTriangle className="h-3.5 w-3.5" /> {teile.join(' · ')}
      </span>
    );
  }
  if (stand.offen === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> geprüft
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{stand.offen} offen</span>;
}

/** Hat die Story Tickets, die noch nicht abgeschlossen sind (offen, Nachtest, behoben, zurückgestellt)? */
const hatMeldungen = (story: FreigabeStoryDto): boolean =>
  story.tickets.open + (story.tickets.nachtest ?? 0) + (story.tickets.behoben ?? 0) + (story.tickets.backlog ?? 0) > 0 ||
  story.tickets.refs.length > 0;

/**
 * Die Meldungen zur Story — dieselbe Liste wie auf der Story-Seite, erst beim
 * Aufklappen geladen. Gelingt das nicht, bleiben wenigstens die Nummern stehen.
 */
function StoryMeldungen({ story }: { story: FreigabeStoryDto }) {
  const [liste, setListe] = useState<{ tickets: StoryTicketDto[]; fremd: FremdTicketDto[] } | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    let aktiv = true;
    abnahmeApi
      .story(story.id)
      .then((detail) => {
        if (aktiv) setListe({ tickets: detail.ticketList ?? [], fremd: detail.fremdTickets ?? [] });
      })
      .catch(() => {
        if (aktiv) setFehler(true);
      });
    return () => {
      aktiv = false;
    };
  }, [story.id]);

  return (
    <div>
      <p className="mb-2 text-sm font-medium">Meldungen</p>
      {liste ? (
        <StoryTicketListe tickets={liste.tickets} fremdTickets={liste.fremd} />
      ) : fehler ? (
        <p className="text-sm text-muted-foreground">{story.tickets.refs.join(', ') || 'Keine Details verfügbar.'}</p>
      ) : (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Meldungen werden geladen …
        </p>
      )}
    </div>
  );
}

function StoryZeile({
  story,
  offen,
  onToggle,
  darfEntscheiden,
  darfPruefen,
  onGeprueft,
  onEntscheiden,
  arbeitet,
}: {
  story: FreigabeStoryDto;
  offen: boolean;
  onToggle: () => void;
  darfEntscheiden: boolean;
  darfPruefen: boolean;
  onGeprueft: () => Promise<void>;
  onEntscheiden: (story: FreigabeStoryDto, entscheidung: 'abgenommen' | 'abgelehnt', grund?: string) => Promise<void>;
  arbeitet: boolean;
}) {
  const [grund, setGrund] = useState<string | null>(null);
  const stand = kriterienStand(story);

  return (
    <li className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={offen}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-muted/50"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${offen ? 'rotate-90' : ''}`}
        />
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{story.key}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{story.title}</span>
        <span className="hidden w-24 shrink-0 sm:block">
          <StandBalken stand={stand} />
          <span className="mt-0.5 block text-right text-[11px] tabular-nums text-muted-foreground">
            {stand.geprueft}/{stand.gesamt}
          </span>
        </span>
        <span className="w-28 shrink-0 text-right">
          <StandHinweis story={story} stand={stand} />
        </span>
      </button>

      {offen && (
        <div className="space-y-4 border-t px-4 pb-4 pt-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{story.epic.title}</span>
            {story.readyVersion && <Badge variant="secondary">Version {story.readyVersion}</Badge>}
          </div>
          {story.description && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{story.description}</p>
          )}
          {story.kriterien.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium">Akzeptanzkriterien</p>
              <KriterienPruefung kriterien={story.kriterien} darfPruefen={darfPruefen} onGeprueft={onGeprueft} />
            </div>
          )}
          <p
            className={`text-sm ${story.tests.status === 'rot' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}
          >
            {pruefSatz(story)}
            {story.tickets.open > 0 &&
              ` ${story.tickets.open} offene ${story.tickets.open === 1 ? 'Meldung' : 'Meldungen'}` +
                (story.tickets.blocker > 0 ? `, davon ${story.tickets.blocker} Blocker.` : '.')}
            {(story.tickets.nachtest ?? 0) > 0 &&
              ` ${story.tickets.nachtest} ${story.tickets.nachtest === 1 ? 'Fix wird' : 'Fixes werden'} gerade nachgetestet.`}
          </p>

          {hatMeldungen(story) && <StoryMeldungen story={story} />}

          {darfEntscheiden &&
            (grund !== null ? (
              <div className="space-y-2">
                <Label htmlFor={`grund-${story.id}`}>Was fehlt oder stimmt nicht?</Label>
                <Textarea
                  id={`grund-${story.id}`}
                  rows={3}
                  autoFocus
                  value={grund}
                  onChange={(event) => setGrund(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    disabled={arbeitet || !grund.trim()}
                    onClick={() => void onEntscheiden(story, 'abgelehnt', grund.trim())}
                  >
                    Ablehnen
                  </Button>
                  <Button variant="ghost" onClick={() => setGrund(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button className="gap-1.5" disabled={arbeitet} onClick={() => void onEntscheiden(story, 'abgenommen')}>
                  <CheckCircle2 className="h-4 w-4" /> Abnehmen
                </Button>
                <Button variant="outline" className="gap-1.5" disabled={arbeitet} onClick={() => setGrund('')}>
                  <XCircle className="h-4 w-4" /> Ablehnen …
                </Button>
              </div>
            ))}
        </div>
      )}
    </li>
  );
}

/** Bereichsliste links: je Epic offene Stories und geprüfte Kriterien. */
function BereichsNavigation({
  bereiche,
  gewaehlt,
  onWaehlen,
  alleAnzahl,
}: {
  bereiche: Bereich[];
  gewaehlt: string | null;
  onWaehlen: (key: string | null) => void;
  alleAnzahl: number;
}) {
  const eintrag = (aktiv: boolean) =>
    `w-full rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
      aktiv ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`;
  return (
    <nav aria-label="Bereiche" className="space-y-0.5">
      <button type="button" className={eintrag(gewaehlt === null)} onClick={() => onWaehlen(null)}>
        <span className="flex items-center justify-between gap-2">
          <span>Alle Bereiche</span>
          <span className="tabular-nums text-xs">{alleAnzahl}</span>
        </span>
      </button>
      {bereiche.map((bereich) => {
        const stand = summe(bereich.stories);
        return (
          <button
            key={bereich.key}
            type="button"
            className={eintrag(gewaehlt === bereich.key)}
            onClick={() => onWaehlen(bereich.key)}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="truncate">{bereich.title}</span>
              <span className="tabular-nums text-xs">{bereich.stories.length}</span>
            </span>
            <StandBalken stand={stand} className="mt-1.5" />
          </button>
        );
      })}
    </nav>
  );
}

function EntschiedenListe({ stories }: { stories: FreigabeStoryDto[] }) {
  if (stories.length === 0) {
    return <p className="text-sm text-muted-foreground">Noch keine Entscheidung.</p>;
  }
  return (
    <div className="space-y-6">
      {nachBereich(stories).map((bereich) => (
        <section key={bereich.key} className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">{bereich.title}</h3>
          <ul className="divide-y rounded-lg border">
            {bereich.stories.map((story) => (
              <li key={story.id} className="flex flex-wrap items-start gap-3 p-3 text-sm">
                <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{story.key}</span>
                <span className="min-w-0 flex-1">
                  {story.title}
                  {story.letzteFreigabe?.begruendung && story.status === 'abgelehnt' && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      „{story.letzteFreigabe.begruendung}“
                      {story.letzteFreigabe.ticketRef ? ` · Ticket ${story.letzteFreigabe.ticketRef}` : ''}
                    </span>
                  )}
                </span>
                <StoryStatusBadge status={story.status} />
                <span className="w-full text-xs text-muted-foreground sm:w-auto">
                  {story.letzteFreigabe
                    ? `${story.letzteFreigabe.name ?? 'unbekannt'} · ${datum(story.letzteFreigabe.createdAt)} · Version ${story.letzteFreigabe.version ?? '—'}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Freigabe: Stories, die zur Abnahme bereitstehen, abnehmen oder ablehnen
 * (docs/abnahme-plan.md, Phase 4).
 *
 * Geprüft wird auf dem Testsystem, hier wird entschieden. Gegliedert nach
 * Bereichen (Epics); jede Story ist zugeklappt eine Zeile mit ihrem Prüfstand
 * und aufgeklappt Kriterien und Entscheidung. Bereich, Filter, Suche und die
 * offene Story stehen in der Adresse, damit sich ein Stand verlinken lässt.
 */
export default function FreigabePage() {
  const darfEntscheiden = usePermission(P.abnahme.freigabe.freigabe_erteilen);
  const darfPruefen = usePermission(P.abnahme.testlaeufe.testlaeufe_durchfuehren);
  const [daten, setDaten] = useState<FreigabeUebersichtDto | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [arbeitet, setArbeitet] = useState<number | null>(null);
  const [params, setParams] = useSearchParams();

  const bereich = params.get('bereich');
  const filter = (FILTER.find((f) => f.key === params.get('filter'))?.key ?? 'alle') as Filter;
  const suche = params.get('suche') ?? '';
  const ansicht = params.get('ansicht') === 'entschieden' ? 'entschieden' : 'offen';
  const offeneStory = params.get('story');

  const setzen = useCallback(
    (werte: Record<string, string | null>) => {
      setParams(
        (alt) => {
          const neu = new URLSearchParams(alt);
          for (const [key, wert] of Object.entries(werte)) {
            if (wert === null || wert === '') neu.delete(key);
            else neu.set(key, wert);
          }
          return neu;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setDaten(await abnahmeApi.freigabe());
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Die Freigabe ließ sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const bereit = useMemo(() => daten?.bereit ?? [], [daten]);
  const entschieden = daten?.entschieden ?? [];
  const bereiche = useMemo(() => nachBereich(bereit), [bereit]);

  const sichtbar = useMemo(
    () =>
      bereit.filter(
        (story) =>
          (bereich === null || story.epic.key === bereich) &&
          passtZumFilter(story, filter) &&
          passtZurSuche(story, suche.trim()),
      ),
    [bereit, bereich, filter, suche],
  );

  const entscheiden = async (story: FreigabeStoryDto, entscheidung: 'abgenommen' | 'abgelehnt', grund?: string) => {
    setArbeitet(story.id);
    // Nach der Entscheidung gleich die nächste Story öffnen — so geht man einen Bereich der Reihe nach durch.
    const index = sichtbar.findIndex((s) => s.id === story.id);
    const naechste = sichtbar[index + 1] ?? null;
    try {
      const ergebnis = await abnahmeApi.entscheiden(story.id, entscheidung, grund);
      toast.success(
        entscheidung === 'abgenommen'
          ? `${story.key} abgenommen.`
          : `${story.key} abgelehnt${ergebnis?.ticketRef ? ` — Ticket ${ergebnis.ticketRef} angelegt` : ''}.`,
      );
      setzen({ story: naechste?.key ?? null });
      await laden();
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Die Entscheidung wurde nicht übernommen.');
    } finally {
      setArbeitet(null);
    }
  };

  if (laedt && !daten) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Freigabe wird geladen …
      </div>
    );
  }

  const zeileFuer = (story: FreigabeStoryDto) => (
    <StoryZeile
      key={story.id}
      story={story}
      offen={offeneStory === story.key}
      onToggle={() => setzen({ story: offeneStory === story.key ? null : story.key })}
      darfEntscheiden={darfEntscheiden}
      darfPruefen={darfPruefen}
      onGeprueft={laden}
      onEntscheiden={entscheiden}
      arbeitet={arbeitet === story.id}
    />
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Freigabe</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Was fertig gemeldet ist und abgenommen werden kann. Geprüft wird auf dem Testsystem
          {daten?.testsystemUrl ? (
            <>
              {' '}
              (
              <a href={daten.testsystemUrl} target="_blank" rel="noreferrer" className="underline">
                öffnen
              </a>
              )
            </>
          ) : null}
          ; die Entscheidung bleibt hier mit Name, Zeitpunkt und Version nachweisbar.
        </p>
      </div>

      {daten && <Gesamtstand daten={daten} />}

      <Tabs value={ansicht} onValueChange={(wert) => setzen({ ansicht: wert === 'offen' ? null : wert, story: null })}>
        <TabsList>
          <TabsTrigger value="offen">Zur Abnahme ({bereit.length})</TabsTrigger>
          <TabsTrigger value="entschieden">Entschieden ({entschieden.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="offen" className="mt-4">
          {bereit.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              Gerade steht nichts zur Abnahme bereit.
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
              <aside className="lg:sticky lg:top-4 lg:self-start">
                <div className="lg:hidden">
                  <Label htmlFor="freigabe-bereich" className="sr-only">
                    Bereich
                  </Label>
                  <select
                    id="freigabe-bereich"
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                    value={bereich ?? ''}
                    onChange={(event) => setzen({ bereich: event.target.value || null, story: null })}
                  >
                    <option value="">Alle Bereiche ({bereit.length})</option>
                    {bereiche.map((b) => (
                      <option key={b.key} value={b.key}>
                        {b.title} ({b.stories.length})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="hidden lg:block">
                  <BereichsNavigation
                    bereiche={bereiche}
                    gewaehlt={bereich}
                    onWaehlen={(key) => setzen({ bereich: key, story: null })}
                    alleAnzahl={bereit.length}
                  />
                </div>
              </aside>

              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[200px] flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      aria-label="Stories durchsuchen"
                      placeholder="Suchen nach Schlüssel, Titel oder Kriterium …"
                      className="pl-8"
                      value={suche}
                      onChange={(event) => setzen({ suche: event.target.value || null })}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1" role="group" aria-label="Filter">
                    {FILTER.map((f) => (
                      <Button
                        key={f.key}
                        size="sm"
                        variant={filter === f.key ? 'secondary' : 'ghost'}
                        aria-pressed={filter === f.key}
                        onClick={() => setzen({ filter: f.key === 'alle' ? null : f.key })}
                      >
                        {f.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {sichtbar.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                    Keine Story passt zu Bereich, Filter und Suche.
                  </div>
                ) : bereich !== null ? (
                  <ul className="space-y-2">{sichtbar.map(zeileFuer)}</ul>
                ) : (
                  nachBereich(sichtbar).map((gruppe) => (
                    <section key={gruppe.key} className="space-y-2">
                      <h2 className="flex items-baseline justify-between text-sm font-medium">
                        <span>{gruppe.title}</span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {gruppe.stories.length} {gruppe.stories.length === 1 ? 'Story' : 'Stories'}
                        </span>
                      </h2>
                      <ul className="space-y-2">{gruppe.stories.map(zeileFuer)}</ul>
                    </section>
                  ))
                )}

                {!darfEntscheiden && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ExternalLink className="h-3 w-3" /> Abnehmen und ablehnen dürfen Super-Admins.
                  </p>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="entschieden" className="mt-4">
          <EntschiedenListe stories={entschieden} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
